/**
 * The recogniser, kept out of the menu.
 * ---------------------------------------------------------------------------
 * Listening used to belong to the row that started it, which is the one place
 * it cannot live: the row is inside a menu, and the first thing the browser
 * does when asked for the microphone is put a permission prompt on top of the
 * page. The menu loses the focus, closes, the row unmounts, and the recogniser
 * is torn down with it - so the ask never heard a word, and from the outside
 * the button simply did nothing.
 *
 * So the recogniser lives here, in a module: one of them, owned by nobody,
 * outliving whatever opened it. Anything that wants to know what it is doing
 * subscribes; the pill that appears while it listens is drawn by whoever is
 * mounted, and if that is nothing at all the words still arrive.
 *
 * ---------------------------------------------------------------------------
 * And it listens until it is TOLD to stop, which is the whole of the second
 * lesson. A SpeechRecognition session is not a microphone: left to itself
 * Chrome ends it at the first pause, and after a few seconds of quiet it
 * raises `no-speech` and gives up. Both are the engine's business, not the
 * person's - someone who presses the shortcut and then thinks for a moment
 * about what to ask has done nothing wrong, and a recogniser that has already
 * closed by the time they speak is a feature that "does not work".
 *
 * So one press opens a SITTING, and the sitting outlives the sessions inside
 * it: every time the engine ends one, another is started, the words heard so
 * far are kept, and the pill never moves. Nothing closes the sitting but the
 * stop button, Enter, Escape, a microphone that is actually refused - or the
 * two guards at the bottom of this file, against a hot microphone left open
 * and against an engine that will not start at all.
 * ---------------------------------------------------------------------------
 */

const Recognition = typeof window !== "undefined"
  && (window.SpeechRecognition || window.webkitSpeechRecognition);

/**
 * Said out loud, in the console.
 *
 * Asking by voice is the one feature in the product with no trace of itself:
 * if nothing happens there is nothing to look at, and "it did not work" covers
 * a microphone that was refused, an engine that heard nothing, words that were
 * heard perfectly and matched no page, and a page that had nowhere to put
 * them. So every step says what it did, under one prefix.
 */
export const askLog = (...what) => {
  try { console.log("%c[ask]", "color:#f6400d;font-weight:700", ...what); }
  catch (e) { /* a console that will not take it is not worth failing over */ }
};

export const voiceSupported = () => !!Recognition;

/* "idle" | "listening" | "working" | "typing" | "denied" | "failed" | "offline" */
let state = "idle";
/* what is on the pill: everything final so far, plus the words still arriving */
let heard = "";
let rec = null;
let onDone = null;

/* Does the person still want to be listening? The engine's `onend` asks this,
   and it is the only thing that decides whether another session starts. */
let wanted = false;
/* Everything final, across every session in this sitting. */
let said = "";
/* A session that ends the instant it starts, over and over, is an engine that
   cannot run - a microphone in use elsewhere, a browser that will not grant
   it. Counted, so the pill can say so instead of flickering forever. */
let stillborn = 0;
let openedAt = 0;
let restartAt = null;

/* Chrome does not recognise speech on the machine unless it is asked to: it
   streams the audio to a service of Google's and reads the answer back. When
   that service cannot be reached the engine says `network` - and it will say
   it again, instantly, for as long as anything keeps starting new sessions.
   Counted, so three of them end the sitting instead of a thousand. */
let networkFails = 0;
const NETWORK_LIMIT = 3;

/* Whether this browser can recognise speech ON the machine, which needs no
   service at all. Asked once per sitting; `null` until it has been. */
let locally = null;

/* The standing way to act on an ask, whatever it arrived by. The voice path
   hands its words to this, and so does anything typed into the pill. */
let handler = null;

/* How quickly a session has to end to count as having never started. */
const STILLBORN = 350;
const GIVE_UP_AFTER = 6;
/* ...and the far end: a sitting nobody closed. Two minutes is longer than any
   question and short enough that a forgotten pill is not an open microphone
   for the rest of the day. */
const MAX_SITTING = 120000;

const listeners = new Set();

function tell() {
  listeners.forEach(fn => {
    try { fn({ state, heard }); } catch (e) { /* a listener that throws is its own problem */ }
  });
}

export function subscribeVoice(fn) {
  listeners.add(fn);
  fn({ state, heard });
  return () => listeners.delete(fn);
}

export function voiceState() {
  return { state, heard };
}

/**
 * What to do with an ask, once there is one.
 *
 * Registered once by whoever knows how to act - the rail - rather than passed
 * in with each sitting, because the words can also arrive typed, from the pill,
 * which has no idea what a route is.
 */
export function setAskHandler(fn) {
  handler = fn;
}

/** Act on an ask that was typed rather than spoken. */
export function askText(text) {
  const asked = String(text || "").trim();
  if (!asked) return false;
  askLog("typed:", `"${asked}"`);
  state = "idle";
  heard = "";
  tell();
  if (handler) handler(asked);
  return true;
}

/** Ask without speaking: the pill, with a field in it. */
export function startTyping(why) {
  cancelVoice();
  state = why || "typing";
  heard = "";
  tell();
  askLog(why === "offline"
    ? "speech is unavailable — type it instead"
    : "type what you want to see");
}

/**
 * The pill, while something is being worked out about what was said.
 *
 * The butler takes a second or two to decide what to do with a sentence, and a
 * second of nothing at all after speaking is a second in which people press
 * the key again. Pass null to put it away.
 */
export function voiceWorking(what) {
  if (!what) {
    if (state === "working") { state = "idle"; heard = ""; tell(); }
    return;
  }
  state = "working";
  heard = what;
  tell();
}

/** Put the pill away without acting on anything. */
export function dismissVoice() {
  if (state === "listening") { cancelVoice(); return; }
  state = "idle";
  heard = "";
  tell();
}

/**
 * Can this browser recognise speech on the machine?
 *
 * Chrome grew an on-device mode (`processLocally`, with a model it downloads
 * once), and where it exists it is the better answer in every way: nothing
 * leaves the machine and nothing depends on a service being reachable. It is
 * asked for by feature test only - every name here is checked before it is
 * called, because this is young API and an older browser has none of it.
 */
async function canWorkLocally() {
  try {
    const SR = Recognition;
    const lang = navigator.language || "en-US";
    const ask = SR.available || SR.availableOnDevice;
    if (typeof ask !== "function") return false;
    const answer = await ask.call(SR, { langs: [lang], processLocally: true });
    if (answer === "available" || answer === true) return true;
    /* there but not here yet: start the download for next time, and use the
       service for this sitting */
    if (answer === "downloadable" || answer === "downloading") {
      const install = SR.install || SR.installOnDevice;
      if (typeof install === "function") {
        askLog("fetching an on-device speech model for next time");
        install.call(SR, { langs: [lang], processLocally: true }).catch(() => {});
      }
    }
    return false;
  } catch (e) {
    return false;
  }
}

/** Everything heard in this sitting, delivered once, at the end of it. */
function deliver() {
  /* `said` is what the engine called final. `heard` is that plus whatever it
     was still working on - and pressing Stop mid-phrase is the normal way to
     use this, so the words in flight are the ones that matter most. Taking the
     longer of the two keeps them without ever losing a finished phrase. */
  const text = (heard.trim().length > said.trim().length ? heard : said).trim();
  const done = onDone;
  onDone = null;
  said = "";
  heard = "";
  if (state === "listening") state = "idle";
  tell();
  if (!text) { askLog("nothing was heard"); return; }
  askLog("heard it all:", `"${text}"`);
  if (done) done(text);
}

/** One session of the engine's. Several of these make one sitting. */
function spin() {
  const startedAt = Date.now();
  rec = new Recognition();
  rec.lang = navigator.language || "en-US";
  /* Keep the session open through a pause rather than closing on it: a
     sentence with a comma in it is still one sentence. */
  rec.continuous = true;
  /* on the machine where the browser can, which is the one way past a speech
     service that cannot be reached */
  if (locally) { try { rec.processLocally = true; } catch (e) { /* older build */ } }
  /* interim results, so something is on screen while the sentence is still
     being said - a mic that shows nothing for three seconds is a mic nobody
     trusts */
  rec.interimResults = true;
  rec.maxAlternatives = 1;

  rec.onresult = (e) => {
    let interim = "";
    let final = "";
    for (let i = e.resultIndex; i < e.results.length; i += 1) {
      const r = e.results[i];
      if (r.isFinal) final += r[0].transcript; else interim += r[0].transcript;
    }
    if (final) said = (said + " " + final).replace(/\s+/g, " ").trim();
    heard = (said + " " + interim).trim();
    /* something was heard, so the engine is plainly running */
    stillborn = 0;
    if (final) askLog("heard:", `"${final.trim()}"`);
    else if (interim) askLog("hearing…", `"${heard}"`);
    tell();
  };

  rec.onerror = (e) => {
    const why = e?.error;
    askLog("the engine says:", why || "(no reason given)");
    if (why === "network") networkFails += 1;
    /* The only two that end a sitting: the microphone was refused. Everything
       else - silence, a dropped connection, a session the engine closed on its
       own - is something to carry on through, because the person has not asked
       to stop. */
    if (why === "not-allowed" || why === "service-not-allowed") {
      wanted = false;
      state = "denied";
      tell();
      /* ...and stays until it is dismissed: a message that clears itself after
         four seconds is one nobody finishes reading */
    }
  };

  rec.onend = () => {
    rec = null;
    if (Date.now() - startedAt < STILLBORN) stillborn += 1;

    if (!wanted) { deliver(); return; }

    /* the sitting has run long enough: close it and keep what was heard */
    if (Date.now() - openedAt > MAX_SITTING) { wanted = false; deliver(); return; }

    /* The speech service is not reachable from this machine. Starting another
       session asks the same question of the same absent service, so the
       sitting ends here - and ends somewhere useful, with a field to type the
       question into instead. */
    if (networkFails >= NETWORK_LIMIT) {
      wanted = false;
      askLog("Chrome recognises speech on a service of Google's, and this browser cannot reach it");
      startTyping("offline");
      return;
    }

    /* an engine that will not run at all, said once rather than flickered */
    if (stillborn >= GIVE_UP_AFTER) {
      wanted = false;
      state = "failed";
      askLog("the engine would not start — six sessions ended the moment they began");
      said = "";
      tell();
      return;
    }

    /* ...otherwise this was the engine's decision, not the person's: go again,
       with everything heard so far still on the pill */
    askLog("the engine closed its session — still listening");
    restartAt = setTimeout(() => { if (wanted) spin(); }, 250);
  };

  try {
    rec.start();
  } catch (e) {
    /* start() on a session that has not finished dying: let the one that is
       ending bring the next one up */
    rec = null;
    stillborn += 1;
    if (stillborn >= GIVE_UP_AFTER) {
      wanted = false;
      state = "failed";
      tell();
    } else {
      restartAt = setTimeout(() => { if (wanted) spin(); }, 300);
    }
  }
}

/**
 * Open a sitting: listen until something closes it.
 *
 * @param done  called once, at the end, with everything that was heard
 */
export function startVoice(done) {
  if (!Recognition) return false;
  /* already listening: the shortcut opens a sitting, it does not toggle one -
     what closes it is the stop button (or Enter, or Escape) */
  if (state === "listening") return true;

  onDone = done || handler;
  said = "";
  heard = "";
  stillborn = 0;
  networkFails = 0;
  wanted = true;
  openedAt = Date.now();
  state = "listening";
  tell();

  askLog("listening —", navigator.language || "en-US", "· press Stop when you are done");

  /* Asked once per sitting, and the first session waits for the answer: it is
     a promise that resolves in a tick, and starting on the service only to
     find out a moment later that the machine could have done it is worse. */
  if (locally === null) {
    canWorkLocally().then(yes => {
      locally = yes;
      askLog(yes ? "recognising on this machine" : "recognising through Chrome's speech service");
      if (wanted) spin();
    });
  } else {
    spin();
  }
  return true;
}

/** Stop listening and keep whatever was heard. */
export function stopVoice() {
  if (!wanted && state !== "listening") return;
  askLog("stopped by you");
  wanted = false;
  clearTimeout(restartAt);
  /* there may be no session running at this instant - between two of them -
     in which case there is nothing to wait for */
  if (!rec) { deliver(); return; }
  try { rec.stop(); } catch (e) { deliver(); }
}

/** Stop listening and throw it away. */
export function cancelVoice() {
  if (state === "listening") askLog("cancelled — what was heard is thrown away");
  wanted = false;
  onDone = null;
  clearTimeout(restartAt);
  try { rec?.abort(); } catch (e) { /* already gone */ }
  rec = null;
  state = "idle";
  heard = "";
  said = "";
  tell();
}
